/**
 * useEntitlements Hook - Single Source of Truth for Entitlements
 * 
 * Provides:
 * - Centralized entitlements fetching and caching
 * - Automatic refresh on tier changes
 * - Consistent state across all components
 * - Integration with tester overrides
 * 
 * Usage:
 * const { tier, entitlements, isTester, refreshEntitlements } = useEntitlements();
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { BACKEND_URL, STORAGE_KEYS } from '../config/appConfig';
import { safeGetItem, logApiEvent, updateEntitlementsState } from '../utils/iosCompat';

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Global cache to avoid duplicate fetches across component instances
let globalEntitlementsCache = {
  data: null,
  timestamp: 0,
  userId: null,
  fetchPromise: null
};

/**
 * Clear the global entitlements cache
 * Call this on logout or tier changes
 */
export const clearEntitlementsCache = () => {
  globalEntitlementsCache = {
    data: null,
    timestamp: 0,
    userId: null,
    fetchPromise: null
  };
};

/**
 * useEntitlements Hook
 * 
 * @param {Object} options
 * @param {string} options.userId - User ID to fetch entitlements for
 * @param {boolean} options.enabled - Whether to fetch entitlements (default: true)
 * @param {number} options.refetchInterval - Auto-refetch interval in ms (default: 0 = disabled)
 */
const useEntitlements = ({ userId, enabled = true, refetchInterval = 0 } = {}) => {
  // State
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  
  // Refs
  const mountedRef = useRef(true);
  const refetchTimerRef = useRef(null);
  
  /**
   * Fetch entitlements from API
   * Uses global cache to prevent duplicate requests
   */
  const fetchEntitlements = useCallback(async (forceRefresh = false) => {
    if (!userId || !enabled) {
      setLoading(false);
      return null;
    }
    
    const now = Date.now();
    
    // Check cache validity (unless force refresh)
    if (!forceRefresh && 
        globalEntitlementsCache.userId === userId && 
        globalEntitlementsCache.data && 
        (now - globalEntitlementsCache.timestamp) < CACHE_DURATION) {
      console.log('[useEntitlements] Using cached data');
      if (mountedRef.current) {
        setEntitlements(globalEntitlementsCache.data);
        setLoading(false);
        setError(null);
      }
      return globalEntitlementsCache.data;
    }
    
    // If a fetch is already in progress for this user, wait for it
    if (globalEntitlementsCache.fetchPromise && globalEntitlementsCache.userId === userId) {
      console.log('[useEntitlements] Waiting for existing fetch');
      try {
        const data = await globalEntitlementsCache.fetchPromise;
        if (mountedRef.current) {
          setEntitlements(data);
          setLoading(false);
          setError(null);
        }
        return data;
      } catch (e) {
        // Fall through to make a new request
      }
    }
    
    // Start new fetch
    setLoading(true);
    setError(null);
    
    const fetchPromise = (async () => {
      try {
        const token = safeGetItem(STORAGE_KEYS.SESSION_TOKEN);
        const headers = {
          'Content-Type': 'application/json'
        };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        
        const response = await fetch(`${BACKEND_URL}/api/entitlements/${userId}`, {
          headers
        });
        
        // Log API event for diagnostics
        logApiEvent({
          endpoint: `/api/entitlements/${userId}`,
          method: 'GET',
          status: response.status
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        
        // Update global cache
        globalEntitlementsCache = {
          data,
          timestamp: Date.now(),
          userId,
          fetchPromise: null
        };
        
        // Update diagnostics state
        updateEntitlementsState({
          loaded: true,
          tier: data.tier,
          tierName: data.tier_name,
          isTester: data.is_tester,
          status: data.subscription?.status
        });
        
        return data;
      } catch (err) {
        console.error('[useEntitlements] Fetch error:', err);
        
        logApiEvent({
          endpoint: `/api/entitlements/${userId}`,
          method: 'GET',
          status: 0,
          error: err.message
        });
        
        updateEntitlementsState({
          loaded: false,
          error: err.message
        });
        
        throw err;
      } finally {
        globalEntitlementsCache.fetchPromise = null;
      }
    })();
    
    // Store promise in cache
    globalEntitlementsCache.fetchPromise = fetchPromise;
    globalEntitlementsCache.userId = userId;
    
    try {
      const data = await fetchPromise;
      if (mountedRef.current) {
        setEntitlements(data);
        setLoading(false);
        setLastFetch(new Date());
      }
      return data;
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message);
        setLoading(false);
      }
      return null;
    }
  }, [userId, enabled]);
  
  /**
   * Refresh entitlements (force fetch)
   */
  const refreshEntitlements = useCallback(() => {
    return fetchEntitlements(true);
  }, [fetchEntitlements]);
  
  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    fetchEntitlements();
    
    return () => {
      mountedRef.current = false;
    };
  }, [fetchEntitlements]);
  
  // Auto-refetch interval
  useEffect(() => {
    if (refetchInterval > 0 && enabled && userId) {
      refetchTimerRef.current = setInterval(() => {
        fetchEntitlements(true);
      }, refetchInterval);
    }
    
    return () => {
      if (refetchTimerRef.current) {
        clearInterval(refetchTimerRef.current);
      }
    };
  }, [refetchInterval, enabled, userId, fetchEntitlements]);
  
  // Derived values
  const tier = entitlements?.tier || 'free';
  const tierName = entitlements?.tier_name || 'Free Plaite';
  const tierId = entitlements?.tier_id || 0;
  const isPremium = tier !== 'free';
  const isTester = entitlements?.is_tester || false;
  const testerOverrideActive = entitlements?.tester_override_active || false;
  const entitlementSource = entitlements?.entitlement_source || 'free';
  
  // Subscription info
  const subscription = entitlements?.subscription || {};
  const isTrialing = subscription?.is_trialing || subscription?.is_signup_trial || false;
  const trialDaysRemaining = subscription?.signup_trial_days_remaining || 0;
  
  // Feature access helpers (based on current tier)
  const hasSmartAccess = tierId >= 1; // Smart tier or above
  const hasGeniusAccess = tierId >= 2; // Genius tier
  
  return {
    // Core state
    entitlements,
    loading,
    error,
    lastFetch,
    
    // Derived values
    tier,
    tierName,
    tierId,
    isPremium,
    isTester,
    testerOverrideActive,
    entitlementSource,
    
    // Subscription info
    subscription,
    isTrialing,
    trialDaysRemaining,
    
    // Feature access
    hasSmartAccess,
    hasGeniusAccess,
    
    // Actions
    refreshEntitlements,
    
    // Raw fetch for special cases
    fetchEntitlements
  };
};

export default useEntitlements;
